import type { ProsecutorContactPreference } from '../interfaces/CourtInterfaces';

interface ProsecutorDto {
  Prosecutor_guid: string;
  Prosecutor_Email: string;
  Prosecutor_Name: string;
  Prosecutor_Phone?: string | null;
  Prosecutor_Disposition?: string | null;
  Prosecutor_Traits?: string[];
  Prosecutor_TraitsOtherNegative?: string | null;
  Prosecutor_TraitsOtherPositive?: string | null;
  Prosecutor_PreferredContact?: ProsecutorContactPreference | null;
  Prosecutor_TextNumber?: string | null;
  Court_id: number;
  Prosecutor_Active: boolean;
}

interface FullProsecutorDto extends ProsecutorDto {
  court?: {
    Court_guid: string;
    Court_Name: string;
    Court_Email: string;
    Court_Address: string;
  };
}

interface ProsecutorListResponse {
  message: string;
  prosecutors: FullProsecutorDto[];
  pagination: {
    from: number;
    size: number;
    total: number;
  };
}

class ProsecutorApi {
  static async searchProsecutors(params: {
    from?: number;
    size?: number;
    Prosecutor_Name?: string;
    Prosecutor_Email?: string;
    Court_Name?: string;
    Court_guid?: string;
    Prosecutor_Active?: boolean;
    search?: string;
  }): Promise<ProsecutorListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.from !== undefined) queryParams.append('from', params.from.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.Prosecutor_Name) queryParams.append('Prosecutor_Name', params.Prosecutor_Name);
    if (params.Prosecutor_Email) queryParams.append('Prosecutor_Email', params.Prosecutor_Email);
    if (params.Court_Name) queryParams.append('Court_Name', params.Court_Name);
    if (params.Court_guid) queryParams.append('Court_guid', params.Court_guid);
    if (params.Prosecutor_Active !== undefined) queryParams.append('Prosecutor_Active', params.Prosecutor_Active.toString());
    if (params.search) queryParams.append('search', params.search);

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/prosecutor/list?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch prosecutors' }));
      throw new Error(errorData.message || 'Failed to fetch prosecutors');
    }

    return await response.json();
  }

  static async getProsecutorById(guid: string): Promise<{ message: string; prosecutor: FullProsecutorDto }> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/prosecutor/${guid}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch prosecutor' }));
      throw new Error(errorData.message || 'Failed to fetch prosecutor');
    }

    return await response.json();
  }

  static async updateProsecutor(
    guid: string, 
    prosecutorData: Partial<ProsecutorDto>
  ): Promise<{ message: string; prosecutor: FullProsecutorDto }> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/prosecutor/${guid}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(prosecutorData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update prosecutor' }));
      throw new Error(errorData.message || 'Failed to update prosecutor');
    }

    return await response.json();
  }

  /**
   * Record a prosecutor against a court.
   *
   * `ManagerApi.createProsecutor` calls the same route, but it returns the `CourtInterfaces` shape,
   * which carries no `Court_id` and so cannot be handed to `ProsecutorSelect`. This exists so the
   * case page can create one and select it in the same step; the standalone create screen still
   * uses the older one.
   */
  static async createProsecutor(prosecutorData: {
    Prosecutor_Name: string;
    Prosecutor_Email: string;
    Prosecutor_Phone?: string | null;
    Court_guid: string;
  }): Promise<FullProsecutorDto> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/prosecutor/create-by-court-guid`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ ...prosecutorData, Prosecutor_Active: true }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: 'Failed to create prosecutor' }));
      throw new Error(errorData.message || 'Failed to create prosecutor');
    }

    const data = await response.json();
    return data.prosecutor;
  }

  static async deleteProsecutor(guid: string): Promise<{ message: string }> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/prosecutor/${guid}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete prosecutor' }));
      throw new Error(errorData.message || 'Failed to delete prosecutor');
    }

    return await response.json();
  }
}

export default ProsecutorApi;
export type { ProsecutorDto, FullProsecutorDto, ProsecutorListResponse };
